import { useParams, Link } from 'react-router-dom';
import { useRepository, useRepositoryCommits, useSyncRepository, useRegisterWebhook, useUnregisterWebhook } from '@/hooks/useRepositories';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  GitBranch,
  GitCommit,
  RefreshCw,
  Webhook,
  ExternalLink,
  ArrowLeft,
  Clock,
  User,
  Plus,
  Minus,
  FileCode,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { formatRelativeTime } from '@/utils/helpers';

export default function RepositoryDetail() {
  const { id } = useParams<{ id: string }>();

  // Queries
  const { data: repo, isLoading: repoLoading, error: repoError } = useRepository(id || '');
  const { data: commits, isLoading: commitsLoading, refetch: refetchCommits } = useRepositoryCommits(id || '', 100);

  // Mutations
  const syncMutation = useSyncRepository();
  const registerWebhookMutation = useRegisterWebhook();
  const unregisterWebhookMutation = useUnregisterWebhook();

  const handleSync = async () => {
    if (id) {
      await syncMutation.mutateAsync({ id, commitLimit: 100 });
      refetchCommits();
    }
  };

  const handleToggleWebhook = async () => {
    if (!repo || !id) return;
    if (repo.webhookActive) {
      await unregisterWebhookMutation.mutateAsync(id);
    } else {
      await registerWebhookMutation.mutateAsync(id);
    }
  };

  if (repoLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (repoError || !repo) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-red-500">Repository not found</h2>
        <Link to="/repos" className="text-primary hover:underline mt-2 inline-block">
          Back to repositories
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <Link
            to="/repos"
            className="text-muted-foreground hover:text-primary inline-flex items-center gap-1 text-sm mb-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to repositories
          </Link>
          <div className="flex items-center gap-3">
            <GitBranch className="h-6 w-6 text-muted-foreground" />
            <h1 className="text-3xl font-bold tracking-tight">{repo.name}</h1>
            {repo.gitlabUrl && (
              <a
                href={repo.gitlabUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary"
              >
                <ExternalLink className="h-5 w-5" />
              </a>
            )}
          </div>
          <p className="text-muted-foreground mt-1">{repo.gitlabPath}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSync}
            disabled={syncMutation.isPending}
          >
            {syncMutation.isPending ? (
              <LoadingSpinner className="h-4 w-4 mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Sync
          </Button>
          <Button
            variant={repo.webhookActive ? 'destructive' : 'default'}
            onClick={handleToggleWebhook}
            disabled={registerWebhookMutation.isPending || unregisterWebhookMutation.isPending}
          >
            <Webhook className="h-4 w-4 mr-2" />
            {repo.webhookActive ? 'Remove Webhook' : 'Add Webhook'}
          </Button>
        </div>
      </div>

      {/* Repository Info */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <GitBranch className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Default Branch</span>
            </div>
            <p className="mt-1 font-medium">{repo.defaultBranch}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Last Commit</span>
            </div>
            <p className="mt-1 font-medium">
              {repo.lastCommitAt ? formatRelativeTime(repo.lastCommitAt) : 'Never'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <GitCommit className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Commits</span>
            </div>
            <p className="mt-1 font-medium">{commits?.length || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Webhook className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Webhook</span>
            </div>
            <p className="mt-1 font-medium flex items-center gap-1">
              {repo.webhookActive ? (
                <>
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Active
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 text-muted-foreground" />
                  Inactive
                </>
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Commits List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitCommit className="h-5 w-5" />
            Recent Commits
          </CardTitle>
        </CardHeader>
        <CardContent>
          {commitsLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : !commits?.length ? (
            <div className="text-center py-8 text-muted-foreground">
              <GitCommit className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No commits found</p>
              <p className="text-sm mt-1">Click Sync to fetch commits from GitLab</p>
            </div>
          ) : (
            <div className="space-y-4">
              {commits.map((commit) => (
                <div
                  key={commit.id}
                  className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <a
                          href={commit.webUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium hover:text-primary truncate"
                        >
                          {commit.title}
                        </a>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {commit.authorName}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatRelativeTime(commit.committedAt)}
                        </span>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          {commit.shortSha}
                        </code>
                        {commit.branch && (
                          <span className="flex items-center gap-1">
                            <GitBranch className="h-3 w-3" />
                            {commit.branch}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="flex items-center gap-1 text-green-600">
                        <Plus className="h-3 w-3" />
                        {commit.additions}
                      </span>
                      <span className="flex items-center gap-1 text-red-600">
                        <Minus className="h-3 w-3" />
                        {commit.deletions}
                      </span>
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <FileCode className="h-3 w-3" />
                        {commit.filesChanged}
                      </span>
                    </div>
                  </div>
                  {commit.message && commit.message !== commit.title && (
                    <p className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap line-clamp-3">
                      {commit.message.split('\n').slice(1).join('\n').trim()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
